const API_URL = import.meta.env.PROD
  ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
  : '/api';

async function request(endpoint, options = {}) {
  const token = localStorage.getItem('token');

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers,
  });

  const data = await response.json();

  if (!data.success) {
    throw new Error(data.error || 'Something went wrong');
  }

  return data.data;
}

export const api = {
  // ==================== AUTH ====================

  // DEPRECATED: Password-based login removed in Phase 6.2 - OAuth only now
  // This function is kept for reference but will not work (endpoint deleted)
  login: (identifier, password, rememberMe = false) =>
    request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ identifier, password, remember_me: rememberMe }),
    }),

  // DEPRECATED: Registration via Google SSO only (Phase 6.2)
  // This function is kept for reference but will not work (endpoint deleted)
  register: (email, password, name) =>
    request('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    }),

  getMe: () => request('/auth/me'),

  // ==================== OAUTH ====================

  // Initiate Google OAuth flow - fetches OAuth URL then redirects to Google
  googleSignIn: async () => {
    const API_URL = import.meta.env.PROD
      ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
      : '/api';

    try {
      // Fetch OAuth URL from backend
      const response = await fetch(`${API_URL}/auth/google`);
      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Gagal memulai OAuth');
      }

      // Extract OAuth URL from response and redirect browser to Google
      if (data.data?.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error('OAuth URL tidak ditemukan dalam respons');
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      // Re-throw for calling component to handle
      throw error;
    }
  },

  // OAuth callback is now handled via URL hash redirect (Phase 6.2)
  // Backend redirects to /auth/callback#token=xxx&needs_profile=true/false
  // Frontend extracts token from window.location.hash

  validateNIM: (nim) =>
    request('/auth/validate-nim', {
      method: 'POST',
      body: JSON.stringify({ nim }),
    }),

  getRegistrationStatus: () => {
    // This endpoint doesn't require authentication
    const API_URL = import.meta.env.PROD
      ? 'https://progress-tracker-api.syafri-tuloli.workers.dev/api'
      : '/api';
    return fetch(`${API_URL}/auth/registration-status`)
      .then(res => res.json())
      .then(data => {
        if (!data.success) {
          throw new Error(data.error || 'Failed to check registration status');
        }
        return data.data;
      });
  },

  completeProfile: (data) =>
    request('/auth/complete-profile', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ==================== USERS (Admin) ====================
  getUsers: () => request('/users'),

  getLecturers: () => request('/users/lecturers'),

  getStudents: () => request('/users/students'),

  getUser: (id) => request(`/users/${id}`),

  createUser: (data) =>
    request('/users', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateUser: (id, data) =>
    request(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteUser: (id) =>
    request(`/users/${id}`, { method: 'DELETE' }),

  bulkCreateUsers: (users) =>
    request('/users/bulk', {
      method: 'POST',
      body: JSON.stringify({ users }),
    }),

  unlinkGoogleAccount: (id) =>
    request(`/users/${id}/unlink-google`, {
      method: 'PUT',
    }),

  // ==================== SEMESTERS (Admin) ====================
  getSemesters: () => request('/semesters'),

  getActiveSemester: () => request('/semesters/active'),

  getSemester: (id) => request(`/semesters/${id}`),

  createSemester: (data) =>
    request('/semesters', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateSemester: (id, data) =>
    request(`/semesters/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteSemester: (id) =>
    request(`/semesters/${id}`, { method: 'DELETE' }),

  activateSemester: (id) =>
    request(`/semesters/${id}/activate`, { method: 'PATCH' }),

  archiveSemester: (id) =>
    request(`/semesters/${id}/archive`, { method: 'PATCH' }),

  // ==================== COURSES (Admin) ====================
  getCourses: () => request('/courses'),

  getCourse: (id) => request(`/courses/${id}`),

  createCourse: (data) =>
    request('/courses', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateCourse: (id, data) =>
    request(`/courses/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteCourse: (id) =>
    request(`/courses/${id}`, { method: 'DELETE' }),

  getCourseStudents: (id) => request(`/courses/${id}/students`),

  enrollStudent: (courseId, userId) =>
    request(`/courses/${courseId}/students`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  removeStudent: (courseId, userId) =>
    request(`/courses/${courseId}/students/${userId}`, { method: 'DELETE' }),

  importStudentsInCourse: (courseId, data) =>
    request(`/courses/${courseId}/import`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  // ==================== TOPICS ====================
  getTopicsBySemester: (semesterId) => request(`/topics/semester/${semesterId}`),

  getAvailableTopics: (semesterId) => request(`/topics/semester/${semesterId}/available`),

  getTopic: (id) => request(`/topics/${id}`),

  createTopic: (data) =>
    request('/topics', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateTopic: (id, data) =>
    request(`/topics/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  approveRejectTopic: (id, status) =>
    request(`/topics/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    }),

  deleteTopic: (id) =>
    request(`/topics/${id}`, { method: 'DELETE' }),

  // ==================== MILESTONES (Lecturer) ====================
  getMilestonesBySemester: (semesterId) => request(`/milestones/semester/${semesterId}`),

  getMilestone: (id) => request(`/milestones/${id}`),

  createMilestone: (data) =>
    request('/milestones', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateMilestone: (id, data) =>
    request(`/milestones/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteMilestone: (id) =>
    request(`/milestones/${id}`, { method: 'DELETE' }),

  // Deliverable Types
  addDeliverable: (milestoneId, data) =>
    request(`/milestones/${milestoneId}/deliverables`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateDeliverable: (id, data) =>
    request(`/milestones/deliverables/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  deleteDeliverable: (id) =>
    request(`/milestones/deliverables/${id}`, { method: 'DELETE' }),

  // ==================== GROUPS ====================
  getGroupsBySemester: (semesterId) => request(`/groups/semester/${semesterId}`),

  getMyGroup: (semesterId) => request(`/groups/my/${semesterId}`),

  getGroup: (id) => request(`/groups/${id}`),

  createGroup: (data) =>
    request('/groups', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  updateGroup: (id, data) =>
    request(`/groups/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    }),

  addGroupMember: (groupId, userId) =>
    request(`/groups/${groupId}/members`, {
      method: 'POST',
      body: JSON.stringify({ user_id: userId }),
    }),

  removeGroupMember: (groupId, userId) =>
    request(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),

  transferLeader: (groupId, userId) =>
    request(`/groups/${groupId}/leader`, {
      method: 'PATCH',
      body: JSON.stringify({ new_leader_id: userId }),
    }),

  deleteGroup: (id) =>
    request(`/groups/${id}`, { method: 'DELETE' }),

  // ==================== SUBMISSIONS ====================
  getGroupSubmissions: (groupId) => request(`/submissions/group/${groupId}`),

  getGroupSummary: (groupId) => request(`/submissions/group/${groupId}/summary`),

  createSubmission: (data) =>
    request('/submissions', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  submitForReview: (id) =>
    request(`/submissions/${id}/submit`, { method: 'PATCH' }),

  reviewSubmission: (id, status, feedback) =>
    request(`/submissions/${id}/review`, {
      method: 'PATCH',
      body: JSON.stringify({ status, feedback }),
    }),

  getSemesterSubmissions: (semesterId) => request(`/submissions/semester/${semesterId}`),

  getSemesterProgress: (semesterId) => request(`/submissions/semester/${semesterId}/progress`),

  // ==================== DISCUSSIONS ====================
  getGroupDiscussions: (groupId) => request(`/discussions/group/${groupId}`),

  createDiscussion: (groupId, message) =>
    request('/discussions', {
      method: 'POST',
      body: JSON.stringify({ group_id: groupId, message }),
    }),

  updateDiscussion: (id, message) =>
    request(`/discussions/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ message }),
    }),

  deleteDiscussion: (id) =>
    request(`/discussions/${id}`, { method: 'DELETE' }),

  // ==================== NOTIFICATIONS ====================
  getNotifications: (limit = 50, offset = 0) =>
    request(`/notifications?limit=${limit}&offset=${offset}`),

  getUnreadCount: () => request('/notifications/unread'),

  markAsRead: (id) =>
    request(`/notifications/${id}/read`, { method: 'PATCH' }),

  markAllAsRead: () =>
    request('/notifications/read-all', { method: 'PATCH' }),

  deleteNotification: (id) =>
    request(`/notifications/${id}`, { method: 'DELETE' }),

  // ==================== SYSTEM SETTINGS (Admin) ====================
  getSystemSettings: () => request('/system-settings'),

  getSystemSetting: (key) => request(`/system-settings/${key}`),

  updateSystemSetting: (key, value, description) =>
    request(`/system-settings/${key}`, {
      method: 'PUT',
      body: JSON.stringify({ value, description }),
    }),

  getSettingHistory: (key, limit = 50, offset = 0) =>
    request(`/system-settings/${key}/history?limit=${limit}&offset=${offset}`),

  initializeSystemSettings: () =>
    request('/system-settings/initialize', {
      method: 'POST',
    }),

  // ==================== NOTIFICATION PREFERENCES ====================
  getNotificationPreferences: () => request('/notification-preferences'),

  updateNotificationPreferences: (preferences) =>
    request('/notification-preferences', {
      method: 'PUT',
      body: JSON.stringify(preferences),
    }),
};
